import DeleteIcon from '@mui/icons-material/Delete'
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { useTranslation } from 'react-i18next'

import { ScoreParameterDto, ScoreParameterType } from '../api/services/qualityControlService'
import { FieldSuggestion } from './utils/parseSurveyFields'

interface Props {
  parameters: ScoreParameterDto[]
  onChange: (params: ScoreParameterDto[]) => void
  formSuggestions?: FieldSuggestion[]
}

const ScoreParametersEditor = ({ parameters, onChange, formSuggestions = [] }: Props) => {
  const { t } = useTranslation()
  const updateParam = (index: number, updates: Partial<ScoreParameterDto>) => {
    const next = [...parameters]
    next[index] = { ...next[index], ...updates }
    onChange(next)
  }

  const handleDelete = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index))
  }

  const handleTypeChange = (index: number, event: SelectChangeEvent<ScoreParameterType>) => {
    const newType = event.target.value as ScoreParameterType
    updateParam(index, {
      type: newType,
      penalty: undefined,
      penaltyPerUnit: undefined,
      unitStep: undefined,
      maxPenalty: undefined,
      enumPenalties: undefined,
    })
  }

  const handleEnumKeyChange = (paramIndex: number, entryIndex: number, newKey: string) => {
    const param = parameters[paramIndex]
    const entries = Object.entries(param.enumPenalties ?? {})
    const newEntries: [string, number][] = entries.map(([k, v], i) => (i === entryIndex ? [newKey, v] : [k, v]))
    updateParam(paramIndex, { enumPenalties: Object.fromEntries(newEntries) })
  }

  const handleEnumValueChange = (paramIndex: number, entryIndex: number, newValue: string) => {
    const param = parameters[paramIndex]
    const entries = Object.entries(param.enumPenalties ?? {})
    const newEntries: [string, number][] = entries.map(([k, v], i) =>
      i === entryIndex ? [k, Number(newValue)] : [k, v],
    )
    updateParam(paramIndex, { enumPenalties: Object.fromEntries(newEntries) })
  }

  const handleEnumDeleteEntry = (paramIndex: number, entryIndex: number) => {
    const param = parameters[paramIndex]
    const entries = Object.entries(param.enumPenalties ?? {})
    updateParam(paramIndex, { enumPenalties: Object.fromEntries(entries.filter((_, i) => i !== entryIndex)) })
  }

  const handleEnumAddOption = (paramIndex: number) => {
    const existing = parameters[paramIndex].enumPenalties ?? {}
    updateParam(paramIndex, { enumPenalties: { ...existing, '': 0 } })
  }

  const matrixNames = [...new Set(formSuggestions.filter((s) => s.matrixFieldName).map((s) => s.matrixFieldName!))]

  return (
    <Box>
      {parameters.length === 0 ? (
        <Typography color="text.secondary">{t('qualityControl.noParameters')}</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('qualityControl.type')}</TableCell>
              <TableCell>{t('qualityControl.fieldName')}</TableCell>
              <TableCell>{t('qualityControl.matrixField')}</TableCell>
              <TableCell>{t('qualityControl.penalty')}</TableCell>
              <TableCell>{t('qualityControl.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((param, index) => {
              const isLocked = formSuggestions.some(
                (s) => s.name === param.fieldName && s.matrixFieldName !== undefined,
              )
              return (
                <TableRow key={param.id ?? index}>
                  <TableCell>
                    <Select
                      size="small"
                      value={param.type}
                      onChange={(e: SelectChangeEvent<ScoreParameterType>) => handleTypeChange(index, e)}
                    >
                      <MenuItem value="boolean">boolean</MenuItem>
                      <MenuItem value="numeric">numeric</MenuItem>
                      <MenuItem value="enum">enum</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={formSuggestions.filter((s) => s.suggestedType === param.type)}
                      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
                      getOptionKey={(opt) =>
                        typeof opt === 'string' ? opt : `${opt.name}|${opt.matrixFieldName ?? ''}`
                      }
                      value={param.fieldName}
                      onChange={(_, value) => {
                        if (!value) return
                        if (typeof value === 'string') {
                          updateParam(index, { fieldName: value })
                        } else {
                          const updates: Partial<ScoreParameterDto> = { fieldName: value.name }
                          if (value.matrixFieldName) updates.matrixFieldName = value.matrixFieldName
                          if (value.enumChoices && !Object.keys(param.enumPenalties ?? {}).length) {
                            updates.enumPenalties = Object.fromEntries(value.enumChoices.map((c) => [c, 0]))
                          }
                          updateParam(index, updates)
                        }
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') updateParam(index, { fieldName: value })
                      }}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      disabled={isLocked}
                      options={matrixNames}
                      value={param.matrixFieldName ?? ''}
                      onChange={(_, value) => updateParam(index, { matrixFieldName: value ?? '' })}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') updateParam(index, { matrixFieldName: value })
                      }}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </TableCell>
                  <TableCell>
                    {param.type === 'boolean' && (
                      <TextField
                        size="small"
                        label={t('qualityControl.penalty')}
                        type="number"
                        value={param.penalty ?? ''}
                        onChange={(e) =>
                          updateParam(index, { penalty: e.target.value === '' ? undefined : Number(e.target.value) })
                        }
                        sx={{ width: 100 }}
                      />
                    )}
                    {param.type === 'numeric' && (
                      <Stack direction="row" gap={1}>
                        <TextField
                          label={t('qualityControl.penaltyPerUnit')}
                          type="number"
                          size="small"
                          value={param.penaltyPerUnit ?? ''}
                          onChange={(e) =>
                            updateParam(index, {
                              penaltyPerUnit: e.target.value === '' ? undefined : Number(e.target.value),
                            })
                          }
                          sx={{ width: 110 }}
                        />
                        <TextField
                          label={t('qualityControl.step')}
                          type="number"
                          size="small"
                          value={param.unitStep ?? ''}
                          onChange={(e) =>
                            updateParam(index, { unitStep: e.target.value === '' ? undefined : Number(e.target.value) })
                          }
                          sx={{ width: 90 }}
                        />
                        <TextField
                          label={t('qualityControl.maxPenalty')}
                          type="number"
                          size="small"
                          value={param.maxPenalty ?? ''}
                          onChange={(e) =>
                            updateParam(index, {
                              maxPenalty: e.target.value === '' ? undefined : Number(e.target.value),
                            })
                          }
                          sx={{ width: 110 }}
                        />
                      </Stack>
                    )}
                    {param.type === 'enum' && (
                      <Box>
                        <Table size="small">
                          <TableBody>
                            {Object.entries(param.enumPenalties ?? {}).map(([key, value], entryIndex) => (
                              <TableRow key={key}>
                                <TableCell sx={{ p: 0.5 }}>
                                  <TextField
                                    size="small"
                                    value={key}
                                    onChange={(e) => handleEnumKeyChange(index, entryIndex, e.target.value)}
                                  />
                                </TableCell>
                                <TableCell sx={{ p: 0.5 }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={value}
                                    onChange={(e) => handleEnumValueChange(index, entryIndex, e.target.value)}
                                  />
                                </TableCell>
                                <TableCell sx={{ p: 0.5 }}>
                                  <IconButton size="small" onClick={() => handleEnumDeleteEntry(index, entryIndex)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Button size="small" onClick={() => handleEnumAddOption(index)}>
                          {t('qualityControl.addOption')}
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default ScoreParametersEditor
